import { useEffect, useState } from 'react';
import { Link, Outlet } from 'react-router-dom';
import { connectivity } from '../lib/connectivity';
import {
  clearOrgScopedWorkingState,
  getActiveOrgId,
  getUnsafeSwitchState,
  listMyOrgs,
  setActiveOrgId,
  type OrgMembership,
} from '../lib/org';

export function AppLayout() {
  const [online, setOnline] = useState(connectivity.isOnline());
  const [orgs, setOrgs] = useState<OrgMembership[]>([]);
  const [orgErr, setOrgErr] = useState<string | null>(null);
  const [switching, setSwitching] = useState(false);

  useEffect(() => {
    return connectivity.onStatusChange(setOnline);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const rows = await listMyOrgs();
        if (cancelled) return;
        setOrgs(rows);
        const existing = getActiveOrgId();
        if (!existing) {
          const current = rows.find((row) => row.is_current) ?? rows[0];
          if (current) setActiveOrgId(current.org_id);
        }
      } catch (e) {
        if (!cancelled) setOrgErr((e as Error).message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const dateLabel = new Date().toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
  const activeOrgId = getActiveOrgId();
  const currentOrg = orgs.find((row) => row.org_id === activeOrgId) ?? orgs.find((row) => row.is_current) ?? orgs[0];
  const hasMultipleOrgs = orgs.length > 1;

  async function onOrgChange(nextOrgId: string) {
    if (!nextOrgId || switching) return;
    if (nextOrgId === activeOrgId) return;
    setSwitching(true);
    try {
      const unsafe = await getUnsafeSwitchState();
      if (unsafe.hasUnsyncedItems) {
        const ok = window.confirm(
          `You have ${unsafe.unsyncedCount} unsynced photo item(s) across ${unsafe.affectedJobCount} job(s). Switch org anyway?`,
        );
        if (!ok) return;
      }
      setActiveOrgId(nextOrgId);
      clearOrgScopedWorkingState();
      window.location.assign('/');
    } finally {
      setSwitching(false);
    }
  }

  return (
    <div className="min-h-screen bg-white text-black">
      <header className="sticky top-0 z-30 flex items-center justify-between border-b border-gray-200 bg-white px-4 py-3">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-gray-700">{dateLabel}</div>
          <div className="mt-0.5 flex items-center gap-2">
            <span className="rounded bg-eva-teal/10 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-eva-teal">
              Org
            </span>
            {hasMultipleOrgs ? (
              <select
                aria-label="Active organization"
                value={currentOrg?.org_id ?? ''}
                onChange={(e) => void onOrgChange(e.target.value)}
                disabled={switching}
                className="max-w-[180px] truncate rounded border border-gray-300 bg-white px-2 py-0.5 text-xs font-semibold"
              >
                {orgs.map((org) => (
                  <option key={org.org_id} value={org.org_id}>
                    {org.org_name}
                  </option>
                ))}
              </select>
            ) : (
              <span className="max-w-[180px] truncate text-xs font-semibold text-gray-700">
                {currentOrg?.org_name ?? (orgErr ? 'Org unavailable' : 'Loading…')}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3 text-xs">
          <div className="flex items-center gap-1.5">
            <span
              className={`inline-block h-2 w-2 rounded-full ${
                online ? 'bg-eva-teal' : 'bg-bracket-amber'
              }`}
              aria-hidden
            />
            <span className="opacity-70">{online ? 'online' : 'offline'}</span>
          </div>
          <Link
            to="/settings"
            aria-label="Settings"
            className="rounded-full bg-gray-100 px-2 py-1 text-gray-700"
          >
            ⚙
          </Link>
        </div>
      </header>
      <Outlet />
    </div>
  );
}
